/**
 * The trains session as the backend sends it (backend/backend/domains/trains/plugin/session.py,
 * docs/NEW-UI/protocollo.md §4), read with defaults: a missing field is an empty value, never a crash.
 */
import { z } from 'zod';
import type { Session } from '@platform/core/wire';

export const classPriceSchema = z.object({
  name: z.string(),
  price: z.number(),
  seats: z.number(),
  fare: z.string().nullable().optional(),
}).passthrough();
export type ClassPrice = z.infer<typeof classPriceSchema>;

export const legSchema = z.object({
  train_id: z.string(),
  train_type: z.string(),
  departure_station: z.string(),
  arrival_station: z.string(),
  departure_time: z.string(),
  arrival_time: z.string(),
}).passthrough();

export const trainSchema = z.object({
  train_id: z.string(),
  train_type: z.string(),
  departure_time: z.string(),
  arrival_time: z.string(),
  departure_station: z.string(),
  arrival_station: z.string(),
  duration_min: z.number(),
  price_eur: z.number(),
  class_prices: z.array(classPriceSchema).default([]),
  available_seats: z.number().default(0),
  changes: z.number().default(0),
  legs: z.array(legSchema).default([]),
  platform: z.string().nullable().default(null),
  blocked_reason: z.string().nullable().default(null),
  rolling_stock: z.string().nullable().optional(),
  /** What is on board, as the operator says it ("wifi", "power"…): none said, none drawn. */
  on_board: z.array(z.string()).default([]),
}).passthrough();
export type Train = z.infer<typeof trainSchema>;

export const coachSchema = z.object({
  coach_number: z.number(),
  class_name: z.string(),
  occupied: z.array(z.string()).default([]),
  display_class: z.string().optional(),
  seat_rows: z.array(z.array(z.string())).default([]),
  corridor_after: z.number().default(0),
  columns: z.array(z.string()).default([]),
}).passthrough();
export type Coach = z.infer<typeof coachSchema>;

export interface Seat {
  seat: string;
  coach: string;
}

export interface Trip {
  origin: string | null;
  destination: string | null;
  date: string | null;
  time: string | null;
  adults: number;
  children: number;
  extras: { petsSmall: number; petsBig: number; bikesNormal: number; bikesFoldable: number };
  results: Train[];
  /** The runtime filters that would change the list shown (backend result_filter.narrowing_filters). */
  narrowingFilters: string[];
  selectedIdx: number | null;
  selectedTrain: Train | null;
  selectedClass: string | null;
  selectedFare: string | null;
  seats: Seat[];
  coaches: Coach[];
  orderer: { first_name: string | null; last_name: string | null; email: string | null; phone: string | null };
  passengersDetails: { first_name?: string | null; last_name?: string | null }[];
  pnr: string | null;
  cp: string | null;
  carriage: string | null;
  totalPrice: number | null;
  errorMessage: string | null;
  pageIdx: number;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
const num = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const arrayOf = <T>(schema: z.ZodType<T>, v: unknown): T[] =>
  (Array.isArray(v) ? v : []).flatMap((x) => {
    const r = schema.safeParse(x);
    return r.success ? [r.data] : [];
  });

export function readTrip(s: Session): Trip {
  const pax = (s.passengers ?? {}) as Record<string, unknown>;
  const extras = (s.extras ?? {}) as Record<string, unknown>;
  const orderer = (s.orderer ?? {}) as Record<string, unknown>;
  const selected = trainSchema.safeParse(s.selected_train);
  return {
    origin: str(s.origin),
    destination: str(s.destination),
    date: str(s.date),
    time: str(s.time),
    adults: num(pax.adults, 1),
    children: num(pax.children, 0),
    extras: {
      petsSmall: num(extras.petsSmall), petsBig: num(extras.petsBig),
      bikesNormal: num(extras.bikesNormal), bikesFoldable: num(extras.bikesFoldable),
    },
    results: arrayOf(trainSchema, s.search_results),
    narrowingFilters: arrayOf(z.string(), s.narrowing_filters),
    selectedIdx: typeof s.selected_idx === 'number' ? s.selected_idx : null,
    selectedTrain: selected.success ? selected.data : null,
    selectedClass: str(s.selected_class_display) ?? str(s.selected_class),
    selectedFare: str(s.selected_fare),
    seats: (Array.isArray(s.selected_seats) ? s.selected_seats : []).map((x) => {
      const o = x as Record<string, unknown>;
      return { seat: String(o.seat ?? ''), coach: String(o.coach ?? '') };
    }),
    coaches: arrayOf(coachSchema, s.coach_layouts),
    orderer: {
      first_name: str(orderer.first_name), last_name: str(orderer.last_name),
      email: str(orderer.email), phone: str(orderer.phone),
    },
    passengersDetails: (Array.isArray(s.passengers_details) ? s.passengers_details : []) as Trip['passengersDetails'],
    pnr: str(s.pnr),
    cp: str(s.cp),
    carriage: s.carriage === null || s.carriage === undefined ? null : String(s.carriage),
    totalPrice: typeof s.total_price === 'number' ? s.total_price : null,
    errorMessage: str(s.error_message),
    pageIdx: num(s.page_idx),
  };
}

/** The trains session is the one with these fields: used for recordings older than `run.domain`. */
export function isTrainsSession(s: Session): boolean {
  return 'search_results' in s && 'selected_train' in s;
}
